import React, { useCallback } from 'react';
import {
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  ButtonContainer,
  FormActionContainer,
  FormControlStyled,
  FormRootDiv,
  MaterialLinkStyled,
  RootDiv,
  TextFieldStyled,
  TitleTypography,
} from './styles';
import Link from 'next/link';
import { getRoute } from '@utils';
import { useForm, Controller } from 'react-hook-form';

interface SuggestionDataForm {
  name: string;
  email: string;
  comment: string;
  origin: string;
}

interface SuggestionDataFormProps {
  loading: boolean;
  handleFormSubmit: (suggestionInfo: {
    name: string;
    email: string;
    comment: string;
    origin: string;
  }) => void;
}

export function SuggestionsForm({
  loading,
  handleFormSubmit,
}: SuggestionDataFormProps) {
  const {
    register,
    handleSubmit: handleSubmitForm,
    formState: { errors },
    control,
  } = useForm<SuggestionDataForm>({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      email: '',
      comment: '',
      origin: '',
    },
  });

  const onSubmit = useCallback(
    (data: SuggestionDataForm) => {
      handleFormSubmit(data);
    },
    [handleFormSubmit],
  );

  return (
    <form onSubmit={handleSubmitForm(onSubmit)}>
      <FormRootDiv>
        <TitleTypography variant="h6" color="textPrimary">
          Deja aquí tus comentarios ✍️
        </TitleTypography>
        <Divider />
      </FormRootDiv>
      <RootDiv>
        <TextFieldStyled
          variant="outlined"
          size="small"
          data-testid="name-input"
          {...register('name', { required: true })}
          label="Nombre"
          error={!!errors.name}
          helperText={errors.name ? 'Debe ingresar un nombre' : ''}
        />

        <TextFieldStyled
          variant="outlined"
          data-testid="email-input"
          {...register('email', {
            required: true,
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Formato de email inválido',
            },
          })}
          size="small"
          label="Email"
          error={!!errors.email}
          helperText={
            errors.email?.message ||
            (errors.email ? 'Debe ingresar un email' : '')
          }
        />
        <FormControlStyled fullWidth size="small">
          <InputLabel id="origin">Origen</InputLabel>
          <Controller
            name="origin"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                labelId="origin"
                label="Origen"
                {...field}
                error={!!errors.origin}
              >
                <MenuItem value={'WEB'}>Página web</MenuItem>
                <MenuItem value={'LOCAL'}>Venta en local</MenuItem>
                <MenuItem value={'ML'}>Mercado Libre</MenuItem>
              </Select>
            )}
          />
          {errors.origin && (
            <FormHelperText error>Debe seleccionar un origen</FormHelperText>
          )}
        </FormControlStyled>
        <TextFieldStyled
          variant="outlined"
          data-testid="comment-input"
          {...register('comment', { required: true })}
          size="small"
          label="Sugerencia/reclamo"
          error={!!errors.comment}
          helperText={errors.comment ? 'Debe ingresar un comentario' : ''}
          multiline
          rows={6}
        />
      </RootDiv>
      <FormActionContainer>
        <Link {...getRoute('allSuggestions').toLink()}>
          <MaterialLinkStyled underline="always">
            Ver sugerencias y reclamos anteriores
          </MaterialLinkStyled>
        </Link>
        <ButtonContainer>
          <Button type="submit" variant="contained" fullWidth>
            Enviar
            {loading && <CircularProgress size={20} color="inherit" />}
          </Button>
        </ButtonContainer>
      </FormActionContainer>
    </form>
  );
}
